import { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {useTranslation} from 'react-i18next';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFastForward, faPlay } from '@fortawesome/free-solid-svg-icons';

import bullsEye from '../../../assets/icons/bullseye-arrow-white.svg';

import parrot from '../../../assets/images/parrot_4.png';
import useFetch from '../../../hooks/use-fetch';
import useAnalyticsEventTracker from '../../../hooks/use-analytics';
import HoFNotice from './HoFNotice';
import SocialMediaButtons from '../../UI/SocialShare';

const LessonComplete = () => {
  const {t: translate } = useTranslation();
  const params = useParams();
  const { apiUrl } = window['appConfig'];
  const sceneId = useSelector((state) => state.currentSceneID);
  const location = useSelector((state) => state.location)
  const lesson = parseInt(params.lesson);
  const conversationData = useSelector((state) => state.conversationData);
  const gaEventTracker = useAnalyticsEventTracker(`Lesson ${conversationData.lesson}`);
  const [stats, setStats ] = useState([]);
  const { data: completedData, loading: progressLoading, error: progressError, initFunc: initFetch } = useFetch();
  let nextLesson = lesson + 1;
  const nextSceneId = conversationData.nextSceneId ? conversationData.nextSceneId : null;
  const nextSceneLocation = conversationData.nextSceneLocation ? conversationData.nextSceneLocation : null;
  const isTutorial = location === 'tutorial' ? true : false;
  const [xpClass, setXpClass] = useState('xp-container')

  const statList = stats.map((stat, index) => {
      return (
          <div className='stat' key={index}>
              {stat.icon && stat.icon}
              <span>{stat.number}</span>
              <p className='stat__text'>{stat.text}</p>
          </div>
          )
  })

  const renderButtonOptions = () => {
    if( nextSceneId !== null && nextSceneLocation !== null && lesson === 3 ) {

      //Moving locations/scene, restart at lesson 1
      if( nextSceneId !== sceneId || nextSceneLocation !== location ) {
        nextLesson = 1
      }

      //If next lesson is greater than 3, we've reached end of current scene (lesson 3) so need to restart
      if( nextLesson > 3 ) {
        nextLesson = 1
      }

      return (
        <Fragment>
          <a href={`/conversation/${nextSceneLocation}/${nextSceneId}/${nextLesson}`} className="button button--success">{translate('next')}</a>
          {! isTutorial && <a href={`/conversation/${location}`} className="button button--alt button--tiny">{translate('back-to-conversations')}</a>}
        </Fragment>
      )
    } else {
      //If next lesson is greater than 3, we've reached end of current scene (lesson 3) so need to go back and choose new scene
      if( nextLesson > 3 ) {
        if( ! isTutorial ) {
          return (
            <a href={`/conversation/${location}`} className="button button--success">{translate('choose-new-conversation')}</a>
          )
        }

        if( isTutorial ) {
          return (
            <a href={`/`} className="button button--success">{translate('tutorial-complete')}</a>
          )
        }
      }

      //Navigate to next lesson
      if( nextLesson === 2 || nextLesson === 3 ) {
        return (
          <Fragment>
            <a href={`/conversation/${location}/${sceneId}/${nextLesson}`} className="button button--success">{translate('next')}</a>
            {! isTutorial && <a href={`/conversation/${location}`} className="button button--alt button--tiny">{translate('back-to-conversations')}</a>}
          </Fragment>
        )
      }
    }
  }

  useEffect(() => {
      let fetchData = new URLSearchParams();
      fetchData.append('lesson', lesson);
  
      const fetchOptions = {
          method: 'PUT',
          headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: fetchData,
      };
      const progressUrl = `${apiUrl}/progress/${sceneId}/completed`;

      initFetch({url: progressUrl, options: fetchOptions });
  }, [])

  useEffect(() => {  
    if( completedData ) {
        let statArr = [
            {
                text: translate('attempts'),
                icon: <FontAwesomeIcon icon={faPlay} size="1x" className='stat__icon'/>,
                number: completedData.totalAttempts
            },
            {
                text: translate('accuracy'),
                icon: <img src={bullsEye} alt="Lesson Accuracy" title="Lesson Accuracy" className='stat__icon' />,
                number: `${completedData.lastAccuracy}%`
            },
            {
                text: translate('skips'),
                icon: <FontAwesomeIcon icon={faFastForward} size="1x" className='stat__icon'/>,
                number: completedData.lastSkips
            }
        ];

        setTimeout(function() {
          setXpClass('xp-container xp-container--active');
        },100)
        setStats(statArr);
    } 
  }, [completedData])

  // const resetConversation = () => {
  //     props.returnToStart();
  // }

  useEffect(() => {
    let fetchData = new URLSearchParams();
    fetchData.append('lesson', lesson);

    const fetchOptions = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: fetchData,
    };
    const progressUrl = `${apiUrl}/progress/${sceneId}/completed`;

    initFetch({ url: progressUrl, options: fetchOptions });
    gaEventTracker('Lesson Complete', `${conversationData.location} ${conversationData.sceneName} Lesson ${conversationData.lesson}`)
  }, []);

  return (
    <Fragment>
      <HoFNotice />
      <div className="lesson-complete test">
        <div className="lesson-complete__container">
          <div className='parrot-container'>
            <img src={parrot} alt="golingo Parrot" title="golingo Parrot" />
          </div>
          <div className="container__header">
            <div className="pyro">
              <div className="before"></div>
              <div className="after"></div>
            </div>
            <h2 className="heading">{isTutorial ? translate('tutorial-part-complete', {lesson: lesson}) : translate('lesson-complete')}</h2>
          </div>
          <div className="container__body">
            {progressLoading && <p>{translate('calculating-stats')}</p>}
            {progressError && <p>{translate('stats-error')}</p>}
            {!progressLoading && <div className="lesson-stats">{statList}</div>}
            {!progressLoading && completedData && 
              <div className={xpClass}>
                <div className="xp-line">
                  <p>Previous XP <span>{new Intl.NumberFormat('en').format(parseInt(completedData.totalXp) - parseInt(completedData.lastAwardedXp))}</span></p>
                </div>
                <div className="xp-line">
                  <p>Lesson XP <span>+{completedData.lastAwardedXp}</span></p>
                </div>
                <div className="xp-line">
                  <p className="final-xp">Current XP <span>{new Intl.NumberFormat('en').format(completedData.totalXp)}</span></p>
                </div>
              </div>
            }
            {!progressLoading && completedData && <SocialMediaButtons code={'gllc'} score={completedData.lastAccuracy} extraStyles={{'color': 'white'}}/>}
            {renderButtonOptions()}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default LessonComplete;
